import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { styles, colors, Modal, fmtRole } from './UI';

// Lets admin / leadership pick an account manager (or any user) to preview
// the app as. Server impersonates that user for the duration; click Exit
// in the topbar banner to return to admin view.
export default function ViewAsModal({ onClose }) {
  const { startViewAs, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // /api/admin/users is admin/leadership-only — a SUPERVISOR opening this
    // modal got a 403 and an empty picker even though the server fully
    // supports supervisor impersonation of direct reports (found during the
    // 2026-07-07 manual write-up). Supervisors load the rep lookup instead;
    // the server still enforces WHO they may actually impersonate.
    const isSup = user?.role === 'supervisor' || user?.actual_user?.role === 'supervisor';
    if (isSup) {
      api.listReps().then(rs => {
        setUsers(rs.map(r => ({ ...r, role: 'rep', username: r.full_name, is_active: true })));
      }).catch(e => setError(e.message));
    } else {
      api.listUsers().then(rs => {
        // Reasonable preview targets: account managers, supervisors, leadership.
        // Skip ops (they have a totally different view) and skip self.
        setUsers(rs.filter(u => u.is_active && u.role !== 'ops'));
      }).catch(e => setError(e.message));
    }
  }, []);

  const filtered = filter.trim()
    ? users.filter(u => u.full_name.toLowerCase().includes(filter.toLowerCase()) ||
                       u.username.toLowerCase().includes(filter.toLowerCase()))
    : users;

  const pick = async (u) => {
    try {
      await startViewAs(u.id);
      onClose();
      // Force re-mount of route content
      window.location.href = '/';
    } catch (err) { setError(err.message); }
  };

  return (
    <Modal title="View as Account Manager" onClose={onClose}>
      <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 0 }}>
        Preview the app exactly as the chosen user sees it -- their accessible data,
        their nav items, their role-gated UI. Click Exit Preview in the topbar to come back.
      </p>
      {error && <div style={styles.errorBox}>{error}</div>}
      <input
        style={styles.input}
        placeholder="Filter by name..."
        value={filter}
        onChange={e => setFilter(e.target.value)}
        autoFocus
      />
      <div style={{ marginTop: 12, maxHeight: 360, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: 6 }}>
        {filtered.length === 0 && (
          <div style={{ padding: 12, color: colors.textMuted, fontSize: 13 }}>No users match.</div>
        )}
        {filtered.map(u => (
          <button
            key={u.id}
            type="button"
            onClick={() => pick(u)}
            style={{
              display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', border: 'none', borderBottom: `1px solid ${colors.border}`,
              background: '#fff', cursor: 'pointer', textAlign: 'left'
            }}
          >
            <span>
              <strong>{u.full_name}</strong>
              <span style={{ color: colors.textMuted, fontSize: 12, marginLeft: 8 }}>
                {fmtRole(u.role)}{u.branch_name ? ` · ${u.branch_name}` : ''}
              </span>
            </span>
            <span style={{ color: colors.orange, fontSize: 12, fontWeight: 600 }}>Preview →</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
